<template>
  <div>
    <TsProCardSkeleton
      v-if="procardStore.loading.company_list"
    />

    <div v-else-if="companyListData.length > 0">
      <TsTypography
        weight="semibold"
        append-class="text-natural-grey mb-3 lg:mb-4"
        >{{useTranslation("selectYourCompany" , "Selecteer uw bedrijf.")}}</TsTypography>
      <TsGrid :cols="3" :space="3" :mobile-cols="1" >
        <TsColumn
          v-for="company in companyListData"
          :key="JSON.stringify(company.organization)"
        >
          <TsRadio
            v-model="procardStore.application_form.company_duns"
            :value="company.organization?.duns"
            :checked="
              company.organization?.duns ===
              procardStore.application_form.company_duns
            "
            append-class="border-2 cursor-pointer hover:bg-info/5 w-full items-start border border-grey bg-idle-white p-4 rounded-sm"
            label-class="cursor-pointer"
            :data-testid="'pro-card-application-company-list-radio-button-' + company.organization?.duns"
          >
            <template #label>
              <div>
                <p>{{ company.organization?.primaryName }}</p>
                <p>{{company.organization?.primaryAddress?.streetAddress.line1}}</p>
                <p>
                  {{company.organization?.primaryAddress?.addressLocality.name}},
                  {{company.organization?.primaryAddress?.addressCountry.name}}
                </p>
              </div>
            </template>
          </TsRadio>
        </TsColumn>
      </TsGrid>
    </div>

     <TsTypography
        v-else="companyListData.length === 0"
        weight="semibold"
        append-class="text-natural-grey mb-3 lg:mb-4"
        >{{useTranslation("WeHaveVerifiedYourCompany" , "We hebben je bedrijf kunnen verifiëren.")}}</TsTypography
      >

      <TsProCardFormBottomBar
        @back="emit('back')"
        @next="handleNext"
        @draft="emit('draft')"
        :next-button="{
          disabled: !procardStore.application_form.company_duns,
        }"
        :save-button="{
          isLoading: procardStore.loading.save_button,
        }"
      />
  </div>
</template>

<script setup lang="ts">
const procardStore = useProCardStore();
import { useTranslation } from "~/composables/useTranslation";
import type { ProCardCompanyList } from "~/types/ecom/procard.type";

const TsProCardSkeleton = defineAsyncComponent(
    () => import('~/components/organisms/TsProCardStepperSkeleton.vue')
);

const TsGrid  = defineAsyncComponent(
    () => import('~/components/organisms/TsGrid.vue')
);

const TsRadio = defineAsyncComponent(
    () => import('~/components/atoms/TsRadio.vue')
);
const TsProCardFormBottomBar = defineAsyncComponent(
    () => import('~/components/procard/TsProCardFormBottomBar.vue')
);

type Props = {
  companyList?: any[];
  dataTestid?: string;
};

const props = withDefaults(defineProps<Props>(), {
  companyList: () => [],
});

const emit = defineEmits<{
  back: any;
  draft: any;
  next: [selectedCompany: Record<string, any>];
}>();

const companyListData = ref<ProCardCompanyList[]>([]);
const appRegion = useRuntimeConfig().public.appRegion; 

function handleNext() {
  const completeCompanyData = companyListData.value.find(
    (company: ProCardCompanyList) =>
      company.organization?.duns === procardStore.application_form.company_duns
  );

  emit("next", {selectedCompany: completeCompanyData});
}

onMounted(() => {
  companyListData.value = props.companyList;

  if(!companyListData.value || companyListData.value.length === 0){
    getRelevantCompanyList();
  }
})


async function getRelevantCompanyList() {
  const { company_name_search_item, commerce_number_search_item } =
      procardStore.application_form;
  companyListData.value = await procardStore.getCompanyList(
      company_name_search_item,
      commerce_number_search_item,
      appRegion
  );

  if (companyListData.value.length === 0) {
    emit('back')
  }
}
</script>
